import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {Alert, Table, TableHead, TableRow, TableBody, Typography} from '@mui/material';
import {format, parseISO} from 'date-fns';
import {StyledTableCell, StyledTableRow} from '../../atoms/styled-elements/StyledTableCell';
import {mergeQuery} from '../../Services/querys';
import {getDateRangeOfWeek, formatNumber} from '../../helpers/utils/utils';
import {group} from '../../Stats/api';
import {IndicatorPercent} from '../../atoms/IndicatorPercent/IndicatorPercent';
import {getShiftCostSummary} from './api';

const money = cost => `${cost.currencySymbol}${cost.amount.toLocaleString('en-GB', {
  minimumFractionDigits: 2, maximumFractionDigits: 2
})}${cost.currencySymbol ? '' : ' (currency unspecified)'}`;

const CostFigure = ({value, sales, loading}) => {
  if (loading) return 'Loading…';
  if (!value?.available) return 'Unavailable';
  return <>
    {value.costs.map(cost => <div key={cost.currencySymbol}>{money(cost)}</div>)}
    {value.missingCostShifts > 0
      ? <Typography variant="caption" color="warning.main">Partial · {value.costedShifts}/{value.shiftCount} shifts costed</Typography>
      : value.costs.length === 1 && value.costs[0].currencySymbol === '£' && sales > 0 &&
        <IndicatorPercent number={value.costs[0].amount} comparisonNumber={sales} />}
  </>;
};

export default function SalaryForecast({query, actualData, forecastData}) {
  const enabled = Boolean(query.from && query.to && query.site);
  const {data: summaryResponse, isLoading, error} = useQuery({
    queryKey: ['forecastShiftCosts', query.site, query.from],
    queryFn: () => getShiftCostSummary(query.site, query.from), enabled
  });
  const summary = summaryResponse?.data;
  // Budgets remain manually entered figures; they do not use shift costs.
  const {data: budgets, error: budgetError} = useQuery({
    queryKey: ['forecastSalaryBudgets', query.site, query.from, query.to, query.filter || ''], enabled,
    queryFn: async () => {
      const from = getDateRangeOfWeek(1, new Date(query.from).getFullYear()).from;
      const [weekly, yearly] = await Promise.all([
        group(mergeQuery(query, {group: 'label,type'})),
        group(mergeQuery(query, {group: 'label,type', from}))
      ]);
      const total = response => (response.data || []).reduce((sum, entry) =>
        entry._id.type === 'budget-wage' ? (entry.amount || 0) : sum, 0);
      return {weekly: total(weekly), yearly: total(yearly)};
    }
  });
  const cumulativeLabel = summary?.supported && summary?.since && summary.since > `${summary.since.slice(0, 4)}-01-01`
    ? `Since ${format(parseISO(summary.since), 'd MMM yyyy')}` : 'YTD';
  return <div>
    <h2 className="box-head" style={{margin: '36px 0 6px'}}>Salary</h2>
    {error && <Alert severity="error">Unable to load shift costs. Please refresh to try again.</Alert>}
    {budgetError && <Alert severity="error">Unable to load salary budgets.</Alert>}
    {summary && <Typography variant="body2" color="text.secondary">
      {summary.supported
        ? 'Actual/WTD uses the latest saved shift costs. Forecast uses the last estimate saved before each shift started. WTD includes today’s full shift estimates; missing costs are unavailable or marked partial.'
        : `Salary figures before ${format(parseISO(summary.reportingStartDate), 'd MMM yyyy')} are unavailable.`}
    </Typography>}
    <Table aria-label="Salary summary" style={{margin: '20px 0 21px'}} size="small">
      <TableHead><TableRow>
        <StyledTableCell />
        <StyledTableCell align="right">{summary?.thisWeek ? 'WTD including today' : summary?.future ? 'Actual to date' : 'Actual'}</StyledTableCell>
        <StyledTableCell align="right">Forecast</StyledTableCell>
        <StyledTableCell align="right">Budget</StyledTableCell>
        <StyledTableCell align="right">{cumulativeLabel}</StyledTableCell>
        <StyledTableCell align="right">YTD Budget</StyledTableCell>
      </TableRow></TableHead>
      <TableBody><StyledTableRow>
        <StyledTableCell>
          Salary
          <Typography variant="caption" component="div" sx={{color: 'success.main', textAlign: 'left'}}>
            (% of sales)
          </Typography>
        </StyledTableCell>
        <StyledTableCell align="right"><CostFigure value={summary?.actual} sales={actualData.wtdTotal} loading={enabled && isLoading} /></StyledTableCell>
        <StyledTableCell align="right"><CostFigure value={summary?.forecast} sales={forecastData.forecastTotal} loading={enabled && isLoading} /></StyledTableCell>
        <StyledTableCell align="right">
          {budgets ? `£${formatNumber(budgets.weekly, 2, true)}` : '—'}
          {budgets && forecastData.budgetTotal > 0 && <IndicatorPercent number={budgets.weekly} comparisonNumber={forecastData.budgetTotal} />}
        </StyledTableCell>
        <StyledTableCell align="right"><CostFigure value={summary?.cumulative} loading={enabled && isLoading} /></StyledTableCell>
        <StyledTableCell align="right">
          {budgets ? `£${formatNumber(budgets.yearly, 2, true)}` : '—'}
          {budgets && actualData.bytdTotal > 0 && <IndicatorPercent number={budgets.yearly} comparisonNumber={actualData.bytdTotal} />}
        </StyledTableCell>
      </StyledTableRow></TableBody>
    </Table>
  </div>;
}
